/**
 * Word counts (part 19b log, tables W and S; decision 41): the chapter's, a selection's, and the long-chapter warning's
 * (decision 14: the editor and the import review warn above 20 000 words in one chapter and offer to split it; part 08
 * log, table LC). Pure.
 */
import type { ParsedChapter } from '../parser/parse.ts';
import type { Inline, TextPiece } from '../literal/inline.ts';
import { chapterModel } from '../literal/model.ts';
import type { Block } from '../literal/model.ts';

/** Above this many words a chapter is long (LC1, LC2). */
export const LONG_CHAPTER_WORDS = 20_000;

/** A source range of a chapter: `from` up to `to`. */
export interface SourceRange {
  readonly from: number;
  readonly to: number;
}

const LETTER = /^[\p{L}\p{N}]/u;
const SPACE = /^\s/u;

/** Whether the character at `at` of `text` is whitespace (`\s`): ASCII decided by its code, the rest by the pattern. */
function isSpace(text: string, at: number, code: number): boolean {
  if (code < 0x80) return code === 0x20 || (code >= 0x09 && code <= 0x0d);
  return SPACE.test(text.slice(at, at + 1));
}

/** Whether the code point at `at` of `text` is a letter or digit (`\p{L}`, `\p{N}`): ASCII decided by its code. */
function isLetter(text: string, at: number, code: number): boolean {
  if (code < 0x80) return (code >= 0x30 && code <= 0x39) || ((code | 0x20) >= 0x61 && (code | 0x20) <= 0x7a);
  return LETTER.test(text.slice(at, at + 2));
}

/** Counts words across the text it is given (W1, W2): a run between whitespace and gaps, holding a letter or digit. */
class Counter {
  count = 0;
  /** Whether the last text ended inside a run, and whether that run holds a letter or digit so far. */
  private open = false;
  private letter = false;
  private readonly range: SourceRange | null;

  constructor(range: SourceRange | null) {
    this.range = range;
  }

  /**
   * Visible text, character by character (the decided path of every count: no allocation per run); a run that goes on
   * from the text before it is one word with it (W3).
   */
  text(text: string): void {
    for (let at = 0; at < text.length; at += 1) {
      const code = text.charCodeAt(at);
      if (isSpace(text, at, code)) this.gap();
      else {
        this.open = true;
        this.letter ||= isLetter(text, at, code);
      }
    }
  }

  /** Something that ends a word (W4): whitespace, a line break, an image, a block's edge, a character not selected (S1). */
  gap(): void {
    if (this.open && this.letter) this.count += 1;
    this.open = false;
    this.letter = false;
  }

  /** S1: a piece's characters whose source overlaps the range; the others are gaps. */
  piece(piece: TextPiece): void {
    const { range } = this;
    if (range === null) {
      this.text(piece.text);
      return;
    }
    // A literal piece maps character by character; an escape or entity as a whole (part 04, table V).
    const { length } = piece.text;
    const literal = piece.origin === 'literal' || piece.origin === 'delimiter';
    const start = literal ? Math.max(0, range.from - piece.from) : 0;
    const end = literal ? Math.min(length, range.to - piece.from) : range.from < piece.to && range.to > piece.from ? length : 0;
    if (start >= end) {
      this.gap();
      return;
    }
    if (start > 0) this.gap();
    this.text(piece.text.slice(start, end));
    if (end < length) this.gap();
  }

  inline(content: readonly Inline[]): void {
    for (const item of content) {
      if (item.kind === 'text') for (const piece of item.pieces) this.piece(piece);
      else if (item.kind === 'brackets') this.inline(item.text);
      else if (item.kind === 'soft-break' || item.kind === 'hard-break' || item.kind === 'image') this.gap();
      // What is left holds children: spans and links count their text (W3); a note reference is no text and no gap (W5).
      else if (item.kind !== 'footnote-reference') this.inline(item.children);
    }
  }

  /** Every word of `blocks`; each block ends a word (W4, W6, W7). */
  blocks(blocks: readonly Block[]): void {
    for (const block of blocks) {
      if (block.kind === 'list') for (const item of block.items) this.blocks(item);
      else if (block.kind === 'quote' || block.kind === 'footnote') this.blocks(block.blocks);
      else if (block.kind !== 'scene-break') this.inline(block.content);
      this.gap();
    }
  }
}

/**
 * The words of a chapter (table W; LC3): runs of its visible text (decision 41) between whitespace, line breaks,
 * images and block edges, each holding a letter or digit; notes included, note numbers, markup and titles not. With
 * `range`, only the visible characters whose source overlaps it count, the rest ending words (table S).
 */
export function countWords(chapter: ParsedChapter, range: SourceRange | null = null): number {
  if (range !== null && range.from >= range.to) return 0;
  const counter = new Counter(range);
  counter.blocks(chapterModel(chapter).blocks);
  return counter.count;
}

/** Whether a chapter is long enough to warn about and offer a split (LC1, LC2). */
export function isLongChapter(chapter: ParsedChapter): boolean {
  return countWords(chapter) > LONG_CHAPTER_WORDS;
}
